import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  CarbonFootprintResponse,
  FLOCK_VARIANT,
  Flock,
  FlockModuleState,
  FlockWeekChartFilters,
  FlockWeekDataSet,
  PorphyrioCategories,
  WeekRegistrationFilters,
} from '../../models/flock.model';
import * as flockActions from '../actions/flock.actions';

export const adapter: EntityAdapter<Flock> = createEntityAdapter<Flock>();

export const initialState = <FlockModuleState>{
  flocks: adapter.getInitialState([]),
  flocksForAllLocations: adapter.getInitialState([]),
  selectedFlockWeekData: undefined,
  selectedFlockId: null,
  flockVariant: FLOCK_VARIANT.Layer,
  isLoaded: false,
  porphyrioCategories: null,
  flockWeekChartFilters: null,
  selectedOtherFlockWeekData: undefined,
  weekRegistrationFilters: null,
  isDetailedViewSelected: false,
  sustainabilityNoticeVisible: true,
};
const flockReducer = createReducer(
  initialState,
  on(flockActions.clearFlockState, (state) => ({
    ...state,
    flocks: adapter.removeAll(state.flocks),
    flocksForAllLocations: adapter.removeAll(state.flocksForAllLocations),
    selectedFlockWeekData: undefined as FlockWeekDataSet,
    selectedFlockId: null as number,
    rearingOrganizations: [] as FlockModuleState['rearingOrganizations'],
    isLoaded: false,
    porphyrioCategories: null as PorphyrioCategories,
    flockWeekChartFilters: null as FlockWeekChartFilters,
    selectedOtherFlockWeekData: undefined as FlockWeekDataSet,
    weekRegistrationFilters: null as WeekRegistrationFilters,
    isDetailedViewSelected: false,
  })),
  on(flockActions.setFlockVariant, (state, { variant }) => ({
    ...state,
    flockVariant: variant,
  })),
  on(flockActions.loadFlocksSuccess, (state, { flocks }) => ({
    ...state,
    flocks: adapter.upsertMany(flocks, state.flocks),
    isLoaded: true,
  })),
  on(flockActions.loadFlocksError, (state) => ({
    ...state,
    isLoaded: true,
  })),
  on(flockActions.loadFlocksForAllLocationsSuccess, (state, { flocksForAllLocations }) => ({
    ...state,
    flocksForAllLocations: adapter.upsertMany(flocksForAllLocations, state.flocksForAllLocations),
    isLoaded: true,
  })),
  on(flockActions.loadFlocksForAllLocationsError, (state) => ({
    ...state,
    isLoaded: true,
  })),
  on(flockActions.upsertFlockSuccess, (state, { flock }) => ({
    ...state,
    selectedFlockId: flock.id,
    flocks: adapter.upsertOne(flock, state.flocks),
    isLoaded: true,
  })),
  on(flockActions.loadHatcheriesSuccess, (state, { hatcheries }) => ({
    ...state,
    hatcheries,
  })),
  on(flockActions.loadRearingOrganizationsSuccess, (state, { rearingOrganizations }) => ({
    ...state,
    rearingOrganizations,
  })),
  on(flockActions.loadDeliveryLinesSuccess, (state, { deliveryLines }) => ({
    ...state,
    deliveryLines,
    isLoaded: true,
  })),
  on(flockActions.loadDeliveryLinesError, (state) => ({
    ...state,
    isLoaded: true,
  })),
  on(flockActions.setSelectedFlockId, (state, { id }) => ({
    ...state,
    selectedFlockId: id,
  })),
  on(flockActions.deleteFlockSuccess, (state, { id }) => ({
    ...state,
    flocks: adapter.removeOne(id, state.flocks),
    selectedFlockId:
      !!state.selectedFlockId && state.selectedFlockId === id ? null : state.selectedFlockId,
  })),
  on(flockActions.loadFlockWeekDataOfFlockSuccess, (state, { data }) => ({
    ...state,
    selectedFlockWeekData: data,
  })),
  on(flockActions.loadPorphyrioCategoriesSuccess, (state, { porphyrioCategories }) => ({
    ...state,
    porphyrioCategories,
  })),
  on(flockActions.upsertFlockWeekChartFiltersSuccess, (state, { flockWeekChartFilters }) => ({
    ...state,
    flockWeekChartFilters,
  })),
  on(flockActions.loadFlockWeekDataOfOtherFlockSuccess, (state, { data }) => ({
    ...state,
    selectedOtherFlockWeekData: data,
  })),
  on(flockActions.clearFlockWeekDataOfOtherFlock, (state) => ({
    ...state,
    selectedOtherFlockWeekData: undefined as FlockWeekDataSet,
  })),
  on(flockActions.upsertWeekRegistrationFiltersSuccess, (state, { weekRegistrationFilters }) => ({
    ...state,
    weekRegistrationFilters,
  })),
  on(flockActions.setIsDetailedViewSelectedSuccess, (state, { isDetailedViewSelected }) => ({
    ...state,
    isDetailedViewSelected,
  })),
  on(flockActions.loadFarmResourcesSuccess, (state, { farmResources, flockResources }) => ({
    ...state,
    farmResources,
    flockResources,
    isLoaded: true,
  })),
  on(flockActions.loadCarbonFootprintSuccess, (state, { data }) => ({
    ...state,
    carbonFootprint: data,
  })),
  on(flockActions.resetCarbonFootprint, (state) => ({
    ...state,
    carbonFootprint: undefined as CarbonFootprintResponse,
  })),
  on(flockActions.setSustainabilityNoticeVisibility, (state, { visible }) => ({
    ...state,
    sustainabilityNoticeVisible: visible,
  })),
);

export function reducer(state: FlockModuleState | undefined, action: Action) {
  return flockReducer(state, action);
}
